import React, { useState } from 'react';
import { TodoFormData } from '../types/Todo';
import { generateTasks } from '../services/geminiService';
import Notification from './Notification';

interface AddTodoProps {
  onAdd: (todo: TodoFormData) => void;
  categories: string[];
}

const AddTodo: React.FC<AddTodoProps> = ({ onAdd, categories }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState<TodoFormData>({
    title: '',
    description: '',
    priority: 'medium',
    category: '',
    dueDate: ''
  });
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.title.trim()) {
      onAdd(formData);
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        category: '',
        dueDate: ''
      });
      setIsOpen(false);
    }
  };

  const handleGenerateTasks = async () => {
    if (!aiPrompt.trim()) return;
    
    setIsGenerating(true);
    try {
      const tasks = await generateTasks(aiPrompt);
      
      // Automatically create all generated tasks
      tasks.forEach((taskTitle, index) => {
        const newTask: TodoFormData = {
          title: taskTitle,
          description: `AI-generated task from: "${aiPrompt}"`,
          priority: 'medium',
          category: 'AI Generated',
          dueDate: ''
        };
        
        // Add a small delay between task creations for better UX
        setTimeout(() => {
          onAdd(newTask);
        }, index * 200);
      });
      
      // Clear the prompt and close the form
      setAiPrompt('');
      setIsOpen(false);
      
      // Show success message
      setTimeout(() => {
        setNotification({
          message: `🎉 Successfully created ${tasks.length} AI-generated tasks!`,
          type: 'success'
        });
      }, tasks.length * 200 + 500);
      
    } catch (error) {
      setNotification({
        message: 'Failed to generate tasks. Please check your API key and try again.',
        type: 'error'
      });
    } finally {
      setIsGenerating(false);
    }
  };


  return (
    <>
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
      <div className="mb-6">
      {!isOpen ? (
        <button
          onClick={() => setIsOpen(true)}
          className="btn-primary w-full animate-bounce-in"
        >
          <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add New Task
        </button>
      ) : (
        <div className="card animate-slide-up">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Add New Task</h2>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* AI Task Generation */}
          <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
            <h3 className="font-medium text-gray-900 mb-2">🤖 AI-Powered Task Generation</h3>
            <p className="text-sm text-gray-600 mb-3">
              Describe what you want to accomplish and AI will automatically create relevant tasks for you!
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="e.g., Plan a birthday party, Organize my workspace, Prepare for job interview..."
                className="flex-1 input-field"
              />
              <button
                onClick={handleGenerateTasks}
                disabled={!aiPrompt.trim() || isGenerating}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating ? 'Creating Tasks...' : 'Generate & Create'}
              </button>
            </div>
            
            {isGenerating && (
              <div className="mt-3 p-3 bg-blue-100 rounded-lg">
                <div className="flex items-center gap-2 text-blue-800">
                  <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span className="text-sm font-medium">AI is generating and creating tasks...</span>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Task Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="input-field"
                placeholder="What needs to be done?"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input-field resize-none"
                rows={3}
                placeholder="Add more details (optional)"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                  className="input-field"
                >
                  <option value="low">Low Priority</option>
                  <option value="medium">Medium Priority</option>
                  <option value="high">High Priority</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="input-field"
                  placeholder="Work, Personal, etc."
                  list="categories"
                />
                <datalist id="categories">
                  {categories.map(category => (
                    <option key={category} value={category} />
                  ))}
                </datalist>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date
              </label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                className="input-field"
              />
            </div>

            <div className="flex gap-3">
              <button type="submit" className="btn-primary flex-1">
                Add Task
              </button>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      </div>
    </>
  );
};

export default AddTodo;
